import { format } from "date-fns";
import { User, Robot, Globe, WhatsappLogo, ArrowSquareOut } from "@phosphor-icons/react";

const ChannelIcon = ({ channel }) => {
  if (channel === "whatsapp") {
    return <WhatsappLogo size={14} weight="bold" className="text-[#25D366]" />;
  }
  return <Globe size={14} weight="bold" className="text-[#007AFF]" />;
};

const MessageBubble = ({ message }) => {
  const isUser = message.role === "user";
  const isWhatsApp = message.channel === "whatsapp";
  
  // Color-coded backgrounds by channel
  const userBgClass = isWhatsApp 
    ? "bg-[#25D366]/10 border border-[#25D366]/30" // Green for WhatsApp
    : "bg-[#007AFF]/10 border border-[#007AFF]/30"; // Blue for Website
  
  const assistantBgClass = isWhatsApp
    ? "bg-[#25D366]/5 border border-[#25D366]/20" // Light green for WhatsApp
    : "bg-[#007AFF]/5 border border-[#007AFF]/20"; // Light blue for Website
  
  return (
    <div 
      className={`flex gap-3 ${isUser ? "justify-start" : "justify-start"}`}
      data-testid={`message-${message.id}`}
    >
      <div 
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser 
            ? (isWhatsApp ? "bg-[#25D366]/20" : "bg-[#2A2A2A]") 
            : (isWhatsApp ? "bg-[#25D366]/20" : "bg-[#007AFF]/20")
        }`}
      >
        {isUser ? (
          <User size={16} className={isWhatsApp ? "text-[#25D366]" : "text-[#A0A0A0]"} />
        ) : (
          <Robot size={16} className={isWhatsApp ? "text-[#25D366]" : "text-[#007AFF]"} />
        )}
      </div>
      
      <div className="flex-1 max-w-2xl">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-semibold ${
            isUser 
              ? (isWhatsApp ? "text-[#25D366]" : "text-[#A0A0A0]") 
              : (isWhatsApp ? "text-[#25D366]" : "text-[#007AFF]")
          }`}>
            {isUser ? "Visitor" : "Arya"}
          </span>
          <ChannelIcon channel={message.channel} />
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
            isWhatsApp 
              ? "bg-[#25D366]/20 text-[#25D366]" 
              : "bg-[#007AFF]/20 text-[#007AFF]"
          }`}>
            {isWhatsApp ? "WhatsApp" : "Website"}
          </span>
          <span className="text-xs text-[#707070]">
            {format(new Date(message.created_at), "HH:mm")}
          </span>
        </div>
        
        <div className={`rounded-lg px-3 py-2 ${isUser ? userBgClass : assistantBgClass}`}>
          <p className="text-sm leading-relaxed text-white">{message.content}</p>
        </div>
      </div>
    </div>
  );
};

const MessageHistory = ({ messages, loading, sessionId, handoffSent, handoffBrief }) => {
  if (loading) {
    return (
      <div 
        className="p-6 text-center"
        data-testid={`messages-loading-${sessionId}`}
      >
        <p className="text-[#A0A0A0] text-sm">Loading messages...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div 
        className="p-6 text-center"
        data-testid={`messages-empty-${sessionId}`}
      >
        <p className="text-[#707070] text-sm">No messages in this session</p>
      </div>
    );
  }

  // Count messages by channel
  const websiteCount = messages.filter(m => m.channel === 'website').length;
  const whatsappCount = messages.filter(m => m.channel === 'whatsapp').length;

  return (
    <div 
      className="p-6 space-y-4"
      data-testid={`message-history-${sessionId}`}
    >
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <h3 className="text-sm font-semibold text-[#A0A0A0] uppercase tracking-wider">
          Conversation History
        </h3>
        <span className="text-xs text-[#707070] bg-[#1A1A1A] px-2 py-0.5 rounded">
          {messages.length} messages
        </span>
        {websiteCount > 0 && (
          <span className="text-[10px] bg-[#007AFF]/20 text-[#007AFF] px-2 py-0.5 rounded font-medium">
            {websiteCount} Website
          </span>
        )}
        {whatsappCount > 0 && (
          <span className="text-[10px] bg-[#25D366]/20 text-[#25D366] px-2 py-0.5 rounded font-medium">
            {whatsappCount} WhatsApp
          </span>
        )}
      </div>
      
      <div className="space-y-4">
        {messages.map(message => (
          <MessageBubble key={message.id} message={message} />
        ))}
      </div>

      {handoffSent && handoffBrief && (
        <div 
          className="mt-6 rounded-lg border border-[#BF5AF2]/30 bg-[#BF5AF2]/5 p-4"
          data-testid={`handoff-brief-${sessionId}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <ArrowSquareOut size={16} weight="bold" className="text-[#BF5AF2]" />
            <span className="text-xs font-semibold uppercase tracking-wider text-[#BF5AF2]">
              Arya's handoff brief
            </span>
          </div>
          <p className="text-sm leading-relaxed text-[#E0E0E0]">
            {handoffBrief}
          </p>
        </div>
      )}
    </div>
  );
};

export default MessageHistory;
